export const sizeChartDelimiter = '-'

export const getProductPricelist = (store, product) => {
  return product.markets[store.getters['frontend/currentMarketId']]?.pricesByPricelist[store.getters['frontend/currentPricelistId']]
}

export const getProductPrice = (store, product, key) => {
  return getProductPricelist(store, product)?.[key] || ''
}

export const getLocalizedValue = (store, product, key) => {
  const localeObject = product.localized?.[store.state.frontend.currentLanguageCode] || product
  return localeObject[key] || product[key]
}

/**
 * This is where we do all the magic that formats the maximized centra format into something that we can
 * always work with. Add new things that you want to play with in here.
 */
export const transformProduct = (store, product, config = {}) => {
  if (!product) {
    return null
  }
  if (product._transformed) {
    // Already transformed
    return product
  }
  const delimiter = config.sizeChartDelimiter ? config.sizeChartDelimiter : sizeChartDelimiter
  const sizeOptions = []
  Object.values(product.items).forEach((item) => {
    const nameParts = item.name.split(delimiter)
    nameParts.forEach((label, index) => {
      const itemData = {
        label,
        value: item.item,
        quantity: item.stockByMarket[store.getters['frontend/currentMarketId']]
      }

      // If you have two-dimensional sizes
      if (nameParts.length > 1) {
        sizeOptions[index] = sizeOptions[index] || []
        sizeOptions[index].push(itemData)
      } else {
        sizeOptions.push(itemData)
      }
    })
  })

  const isOnesize = (sizeOptions.length === 1 && sizeOptions[0].label === '')
  const itemsByMarket = product.markets[store.getters['frontend/currentMarketId']]
  const inStock = itemsByMarket?.stockOfAllItems !== 0

  // This removes the need of ugly ass shit in the templates
  const media = product.media.filter(it => it.type === 'image')
    .filter(it => it.type === 'image' && it.sources.original)
    .map(it => it.sources.original.url)

  const relatedProducts = product.relatedProducts?.length ? product.relatedProducts : []

  const shopTheLookFindifyQuery = relatedProducts.length ? {
    q: '',
    rules: [],
    filters: [{
      action: 'include',
      name: 'item_group_id',
      type: 'text',
      values: relatedProducts.map(x => ({ value: x.product }))
    }]
  } : null

  const similarProductsQuery = product.product_type ? {
    q: '',
    rules: [{
      action: 'exclude',
      name: 'id',
      type: 'text',
      values: [{
        value: product.product
      }]
    }],
    filters: [{
      action: 'include',
      name: 'custom_fields.product_type',
      type: 'text',
      values: [{
        value: product.product_type
      }]
    }]
  } : null

  const discounted = getProductPrice(store, product, 'price') !== getProductPrice(store, product, 'priceBeforeDiscount')

  product._transformed = true

  // Right now i omit the actual centraProduct to keep things under control
  return {
    product: product.product, // OK centra
    sku: product.sku,
    badge: product.badge,
    brandName: product.brandName,
    silkProduct: product.silkProduct,
    price: getProductPrice(store, product, 'price'),
    priceAsNumber: getProductPrice(store, product, 'priceAsNumber'),
    priceBeforeDiscount: getProductPrice(store, product, 'priceBeforeDiscount'),
    discounted,
    discountPercent: getProductPrice(store, product, 'discountPercent'),
    name: getLocalizedValue(store, product, 'name'),
    silkProductName: getLocalizedValue(store, product, 'silkProductName'),
    description: getLocalizedValue(store, product, 'description'),
    shortDescription: getLocalizedValue(store, product, 'excerpt'),
    washingInstructions: getLocalizedValue(store, product, 'washing_instructions'),
    descriptionHeader: getLocalizedValue(store, product, 'description_header'),
    descriptionSubHeader: getLocalizedValue(store, product, 'description_sub_header'),
    fit: getLocalizedValue(store, product, 'fit'),
    pattern: getLocalizedValue(store, product, 'pattern'),
    color_swatch: getLocalizedValue(store, product, 'color_swatch'),
    product_type: getLocalizedValue(store, product, 'folderName'),
    collection: product.collection,
    collectionName: product.collectionName,
    sizeOptions,
    isOnesize,
    inStock,
    items: product.items,
    url: product.url,
    videoUrl: product.video_url || null,
    variantName: product.variantName,

    swatchProducts: product.swatchProducts,
    shopTheLookFindifyQuery,
    similarProductsQuery,

    // We need the meta if supplied
    meta: {
      title: getLocalizedValue(store, product, 'metaTitle'),
      description: getLocalizedValue(store, product, 'metaDescription'),
      keywords: getLocalizedValue(store, product, 'metaKeywords')
    },

    // This allows us to hax business logic that we can rely on
    mainImage: media[0],
    thumbnailImage: media[0],
    media // Always an array that can be iterated
  }
}

/**
 * The store should for now keep a basic structure of the product tree and leave
 * all bigger and heavy lookups to route level lookups
 */
export default {
  namespaced: true,
  state () {
    return {
      products: []
    }
  },
  mutations: {
    products (state, products) {
      const transformedProducts = products.map(product => transformProduct(this, product, this.$config))
      state.products = transformedProducts.concat(state.products.filter((item) => {
        return !products.find(product => product.product === item.product)
      }))
    }
  },
  actions: {
    /**
     * Looks up products from centra
     */
    lookupProducts ({ commit }, ids) {
      if (!ids && !ids?.length) {
        return null
      }
      if (Array.isArray(ids)) {
        ids = ids.join(',')
      }
      return this.$backendApi.get(`/products/${ids}`).then((response) => {
        commit('products', response.data)
        return response.data
      }).catch((e) => {
        console.log(`Error in centra-product/lookupProducts - ${ids} - ${e}`)
      })
    },

    /**
     * Looks up all products related to a silk product from centra
     */
    lookupSilkProduct ({ commit }, id) {
      return this.$backendApi.get(`/products/by-silk-product/${id}`).then((response) => {
        commit('products', [response.data])
        return response.data
      }).catch((e) => {
        console.log(`Error in centra-product/lookupSilkProduct - ${id} - ${e}`)
      })
    }
  },
  getters: {
    getProductById: (state, getters) => id => state.products.find(x => x.product === id),
    getProductBySilkId: (state, getters) => id => state.products.find(x => x.silkProduct === id)
  }
}
